import React, { useContext, useEffect } from 'react'
import clsx from 'clsx'
import { useHistory, matchPath, useLocation } from 'react-router-dom'
import {
  List,
  Drawer,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tooltip,
  Typography,
  ListSubheader,
  useMediaQuery, useTheme, Link,
} from '@mui/material'
import { CrudeContext } from '@3m5/crude-frontend/dist/crudeHooks'

import { NavGroup } from '~shared/models/NavigationModels'
import { isNavRouteCrude } from '~utils/isNavRouteCrude'

import { drawerStyles } from '~app/components/NavigationContainer/components/DrawerComponent/DrawerComponentStyles'
import { useAppBarComponentStyles } from '~app/components/NavigationContainer/components/AppBarComponent/AppBarComponentStyles'
import { DrawerProps } from '~app/components/NavigationContainer/NavigationContainer'
import { useGlobalStyles } from '~shared/styles/globalStyles'
import project from '~./project.json'
import { useTranslation } from 'react-i18next'

type Props = DrawerProps & {
  navGroups: NavGroup[]
}

const DrawerComponent: React.FC<Props> = (props) => {
  const { drawerLarge, drawerOpen, useHoverDrawer } = props

  const { t } = useTranslation()
  const gs = useGlobalStyles()
  const styles = drawerStyles(drawerOpen && !useHoverDrawer)
  const s = styles()
  const stylesAppBar = useAppBarComponentStyles()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const history = useHistory()
  const location = useLocation()

  const { methods, version } = useContext(CrudeContext)
  const drawerContent = props.navGroups.map((group, gIndex) => {
    const drawerItems = !group.hideOnNavigation && group.items.map((item, iIndex) => {
      const routeMatchesPath = !!matchPath(location.pathname, {
        path: item.route.path,
        exact: item.route.exact,
      })

      const handleListItemClick = () => {
        history.push(item.route.path)
        if (isMobile) {
          props.toggleDrawerOpen()
        }
        document.querySelector('main')?.scrollTo(0, 0)
        // reset crude table state
        // todo - temporary solution - as soon as the Crude can handle this on its own - remove this
        // if (isNavRouteCrude(item.route)) {
        //   // methods?.resetTableState()
        // }
      }

      const listItem = <ListItem
        className={clsx(s.listItem)}
        button focusRipple
        key={iIndex}
        selected={routeMatchesPath}
        onClick={handleListItemClick}
      >
        <ListItemText primary={item.label} secondary={item.secondaryLabel} style={{ fontSize: 18, fontFamily: 'Calibri Bold', letterSpacing: 0.5 }} disableTypography />
        <ListItemIcon className={s.listIcon}>
          {item.icon}
        </ListItemIcon>
      </ListItem>

      // todo - verstecke tooltip wenn nicht anzuzeigen ist
      return !item.hideOnNavigation
        && (
          item.description
            ? <Tooltip
              key={iIndex}
              disableHoverListener={!item.description}
              title={<Typography>{item.description}</Typography>}
              placement='right'
            >
              {listItem}
            </Tooltip>
            : listItem
        )
    })

    return <React.Fragment key={gIndex}>
      <List
        className={s.list}
        subheader={
          group.label
            ? <ListSubheader
              component='div'
              color='primary'
              className={clsx(s.listSubheader, !drawerLarge && s.listSubheaderHidden)}
              style={{ fontFamily: 'Calibri Bold', letterSpacing: 0.5 }}
            >
              {group.label}
            </ListSubheader>
            : <></>
        }
      >
        {drawerItems}
      </List>
    </React.Fragment>
  })

  return <>
    <Drawer
      open={useHoverDrawer ? drawerOpen : drawerLarge}
      variant={useHoverDrawer ? 'temporary' : 'permanent'}
      anchor='left'
      onClose={useHoverDrawer ? props.toggleDrawerOpen : props.toggleDrawerLarge}
      className={s.drawer}
      classes={{
        paper: clsx(
          !useHoverDrawer && s.drawerPaper,
          !useHoverDrawer && !drawerLarge && s.drawerPaperSmall,
          !useHoverDrawer && drawerLarge && s.drawerPaperLarge,
          useHoverDrawer && s.drawerPaperLarge,
        ),
      }}
      BackdropProps={{
        className: s.drawerBackdrop,

      }}
    >
      <div className={stylesAppBar.appBarSpacer} />
      <div className={s.drawerContent}>
        {drawerContent}
      </div>
      {
        !drawerOpen
          ? <div style={{ fontFamily: 'Calibri Bold', letterSpacing: 0.5 }}>
            <Tooltip
              title={<Typography>Frontend Version: {project.version}&nbsp;&#8211;&nbsp;Crude
                Version: {version}</Typography>}
              placement='right'
            >
              <p className={clsx(gs.marginBottomTiny, gs.alignTextCenter, gs.marginLeftTiny)} style={{ fontSize: '12px' }}>
                <span title='Frontend Version'>{project.version}</span>
                &nbsp;&#8211;&nbsp;
                <span title='Crude Version'>{version}</span>
              </p>
            </Tooltip>
            <p className={clsx(gs.marginBottomTiny, gs.alignTextCenter, gs.marginLeftTiny)} style={{ fontSize: '12px' }}>
              <Link
                href={t('link:dataProtection')} target='_blank' title='Data Protection'
                color='primary'
              >{t('common:dataProtection')}</Link>
              &nbsp;&#8211;&nbsp;
              <Link href={t('link:imprint')} target='_blank' title='Imprint' color='primary'>{t('common:imprint')}</Link>
            </p>
          </div>
          : null
      }
    </Drawer>
  </>
}

export default DrawerComponent
